import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import MeasurementsChart from "@/components/MeasurementsChart";
import BmiTdeeCard from "@/components/BmiTdeeCard";
import { formatItalianDate } from "@/lib/dateFormat";

export default function ClientMeasurements() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!user) return;
    api.get(`/clients/${user.user_id}/measurements`).then(({ data }) => setRows(data));
  }, [user]);

  const sortedDesc = useMemo(
    () => [...rows].sort((a, b) => (b.date || "").localeCompare(a.date || "")),
    [rows]
  );

  return (
    <div data-testid="client-measurements">
      <div className="label-tiny">Progressi</div>
      <h1 className="font-serif text-5xl tracking-tight mt-2">Rilievi corporei</h1>
      <p className="mt-3 text-sm text-[color:var(--text-secondary)] max-w-lg">Il tuo storico nel tempo, con grafici di confronto.</p>

      <div className="mt-8">
        <MeasurementsChart data={rows} gender={user?.gender || "M"} />
      </div>

      <div className="mt-6">
        <BmiTdeeCard client={user} latestMeasurement={sortedDesc[0] || null} />
      </div>

      <div className="mt-8 space-y-3">
        <div className="label-tiny">Tutti i rilievi ({rows.length})</div>
        {rows.length === 0 && <div className="text-sm text-[color:var(--text-secondary)]">Nessun rilievo ancora.</div>}
        {sortedDesc.map((m) => (
          <div key={m.measurement_id} className="border border-[color:var(--border)] rounded-2xl p-5 bg-[color:var(--bg-primary)]">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3">
              <div className="font-sans text-base tabular-nums" data-testid={`client-measurement-date-${m.measurement_id}`}>{formatItalianDate(m.date)}</div>
              <div className="text-xs text-[color:var(--text-secondary)] tabular-nums">
                Peso {m.peso_kg}kg · BF {(m.bodyfat_pct*100).toFixed(1)}% · MM {m.massa_magra_kg}kg
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {[
                ["Avambraccio", m.avambraccio], ["Braccio", m.braccio],
                m.gender === "F" ? ["Glutei", m.glutei] : null,
                ["Vita", m.giro_vita], ["Periomb.", m.periombelicale],
                ["Coscia", m.giro_coscia], ["Vasto med.", m.vasto_mediale],
                ["Polpaccio", m.polpaccio],
              ].filter(Boolean).map(([l, v]) => (
                <div key={`${m.measurement_id}-${l}`} className="border border-[color:var(--border)] rounded-lg p-2.5">
                  <div className="label-tiny">{l}</div>
                  <div className="mt-1 tabular-nums">{v || 0} <span className="text-[color:var(--text-secondary)]">cm</span></div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
